import { useTranslation } from 'react-i18next'
import { Device } from '../types'
import { AssetStatus } from '../types/Asset'
import StatusIndicator from './StatusIndicator'
import { Tooltip } from '@mui/material'
import { Circle } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import { getDevice } from '../services/deviceService'
import { purple } from '@mui/material/colors'

/**
 * The props of the device status indicator.
 */
export interface DeviceStatusIndicatorProps {
  /**
   * The device.
   */
  device: Device

  /**
   * The font size.
   */
  fontSize?: 'inherit' | 'small' | 'medium' | 'large'

  /**
   * Indicates if the status should updated automatically.
   */
  autoUpdate?: boolean

  /**
   * Called when the latest device status is loaded.
   */
  onStatusChange?: (status: boolean) => void
}

/**
 * An indicator for the device status.
 */
export default function DeviceStatusIndicator({
  device,
  fontSize = 'inherit',
  autoUpdate = false,
  onStatusChange
}: DeviceStatusIndicatorProps): React.ReactElement {
  /**
   * The translation function.
   */
  const { t } = useTranslation('common')

  /**
   * The device status.
   */
  const [status, setStatus] = useState<boolean | null>(null)

  useEffect(() => {
    // Drop any previously fetched status when the device (or its prop status) changes,
    // so we don't display a stale value when this component instance is reused.
    setStatus(null)
  }, [device.name, device.status])

  /**
   * Load the status if auto update is enabled for every minute.
   */
  useEffect(() => {
    if (!autoUpdate) return

    const controller = new AbortController()

    const updateStatus = async () => {
      try {
        const { status } = await getDevice(device.name, {
          signal: controller.signal
        })
        setStatus(status)
        onStatusChange?.(status)
      } catch (_error) {
        // Ignore the error.
      }
    }

    const ONE_MINUTE = 60 * 1000
    const interval = setInterval(() => {
      void updateStatus()
    }, ONE_MINUTE)

    return () => {
      controller.abort()
      clearInterval(interval)
    }
  }, [device.name, autoUpdate, onStatusChange])

  return (
    <>
      {device.isVirtual ? (
        <Tooltip title={t('virtualDevice')}>
          <Circle sx={{ color: purple[500] }} fontSize={fontSize} />
        </Tooltip>
      ) : device?.asset?.status === AssetStatus.INACTIVE ? (
        <Tooltip title={t('detached')}>
          <Circle color="disabled" fontSize={fontSize} />
        </Tooltip>
      ) : (
        <StatusIndicator status={status ?? device.status} fontSize={fontSize} />
      )}
    </>
  )
}
